import { useState } from 'react';

import { ChevronDownIcon } from '@/components/Icons';
import { useDockState } from 'views/dock/dock-state';

import { convertBackendTimeToBrowser, formatTime } from '../../../utils';
import { CALENDAR_EVENT_TYPE_RULE_CLASSES, getCalendarEventType } from '../calendar-event-type';
import {
    isShareableNote,
    MEETING_MINUTES_LINK_CLASSES,
    MeetingMinutesRow,
    splitIntoParagraphs,
} from '../meeting-minutes-list-utils';
import { MeetingMinutesNoteSections } from './MeetingMinutesNoteSections';
import { MeetingMinutesShareNote } from './MeetingMinutesShareNote';
import { MeetingMinutesSuggestions } from './MeetingMinutesSuggestions';

const CARD_CLASSES = 'rounded-md border px-3.5 py-2.5';
const ROW_CLASSES = 'grid grid-cols-[128px_minmax(0,256px)_minmax(0,1fr)_112px] items-center gap-3.5';

// One card per row: an event carrying several notes has already been fanned out into a row per
// note, so the card renders exactly one note, or the note-less variant when it has none.
export const MeetingMinutesEventCard = ({ event, note }: MeetingMinutesRow) => {
    const { addCreateNoteDockItem, addUpdateNoteDockItem } = useDockState();
    const [isExpanded, setIsExpanded] = useState(false);

    const timeRange = `${formatTime(convertBackendTimeToBrowser(event.startTime, event.startTimeZone))} - ${formatTime(
        convertBackendTimeToBrowser(event.endTime, event.endTimeZone),
    )}`;

    if (!note) {
        return (
            <div className={`${CARD_CLASSES} ${ROW_CLASSES} border-dashed`}>
                <span className="text-muted-foreground text-xs whitespace-nowrap">{timeRange}</span>
                <span className="text-muted-foreground truncate text-sm" title={event.title}>
                    {event.title}
                </span>
                <span className="col-start-4 flex items-center justify-end">
                    {event.clientHouseholdGUID ? (
                        <button
                            type="button"
                            className={MEETING_MINUTES_LINK_CLASSES}
                            onClick={() => addCreateNoteDockItem(event.clientHouseholdGUID, event.eventGUID)}
                        >
                            Add note
                        </button>
                    ) : (
                        <span className="text-muted-foreground text-xs tracking-wide uppercase">No note</span>
                    )}
                </span>
            </div>
        );
    }

    const summaryPreview = splitIntoParagraphs(note.summary)[0];

    const openNoteDockItem = (viewMode: 'view' | 'edit') =>
        addUpdateNoteDockItem(
            note.noteGUID,
            note.householdGUID,
            { subject: note.subject, content: note.content },
            viewMode,
        );

    return (
        <div
            className={`${CARD_CLASSES} border-l-2 bg-white ${CALENDAR_EVENT_TYPE_RULE_CLASSES[getCalendarEventType(event)]}`}
        >
            <button
                type="button"
                className={`${ROW_CLASSES} w-full cursor-pointer text-left`}
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((expanded) => !expanded)}
            >
                <span className="text-muted-foreground text-xs whitespace-nowrap">{timeRange}</span>
                <span className="text-cocoa-1000 truncate text-sm font-medium" title={event.title}>
                    {event.title}
                </span>
                <span className="text-muted-foreground truncate text-xs" title={summaryPreview}>
                    {summaryPreview}
                </span>
                <span className="flex items-center justify-end">
                    <ChevronDownIcon
                        className={`text-muted-foreground h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                </span>
            </button>
            {isExpanded && (
                <div className="mt-2.5 space-y-4 border-t pt-3">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
                        <MeetingMinutesNoteSections note={note} />
                        <MeetingMinutesSuggestions note={note} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3.5">
                        <button
                            type="button"
                            className={MEETING_MINUTES_LINK_CLASSES}
                            onClick={() => openNoteDockItem('view')}
                        >
                            Open the full note
                        </button>
                        <button
                            type="button"
                            className={MEETING_MINUTES_LINK_CLASSES}
                            onClick={() => openNoteDockItem('edit')}
                        >
                            Edit
                        </button>
                        {isShareableNote(event, note) && <MeetingMinutesShareNote note={note} />}
                    </div>
                </div>
            )}
        </div>
    );
};
